// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface NcaaCalendarEvent {
  id: string;
  title: string;
  description: string | null;
  category: string;
  start_date: string;
  end_date: string | null;
  division: string | null;
  source: string | null;
  sport_id?: string | null;
  sports?: {
    id: string;
    name: string;
    gender: string;
  } | null;
  created_at: string;
}

export const useNcaaCalendarEvents = (sportId?: string) => {
  const [events, setEvents] = useState<NcaaCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch NCAA calendar events from the database
      // Only show current and future events
      let query = supabase
        .from('calendar_events')
        .select(`
          *,
          sports:sport_id (
            id,
            name,
            gender
          )
        `)
        .is('profile_id', null) // Only admin/system events
        .gte('start_date', new Date().toISOString().split('T')[0]) // Only current and future events
        .order('start_date', { ascending: true });

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      // Filter events based on sport if sportId is provided
      let filteredEvents = data || [];
      
      if (sportId && data) {
        // Parse the sportId to get sport name and gender (e.g., "women-basketball" -> "Basketball", "Women") 
        const parts = sportId.split('-');
        const userGender = parts[0] === 'men' ? 'Men' : parts[0] === 'women' ? 'Women' : null;
        const userSport = parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
        
        // Debug the filtering
        console.log('🎯 Filtering NCAA events for sportId:', sportId);
        console.log('📊 Total events before filtering:', data?.length);
        console.log('🏀 User sport:', userSport, 'Gender:', userGender);

        filteredEvents = data.filter(event => {
          // Always include non-sport events
          if (!event.sport_id || event.category === 'testing' || event.category === 'fafsa') {
            console.log('✅ Including general event:', event.title);
            return true;
          }
          
          // For sport events, only include if it matches the user's sport
          const eventSport = event.sports;
          const matchesSport = eventSport && 
            eventSport.name.toLowerCase() === userSport.toLowerCase() && 
            eventSport.gender.toLowerCase() === userGender.toLowerCase();
          
          if (!matchesSport) {
            console.log('❌ Excluding non-matching sport:', event.title, eventSport);
          } else {
            console.log('✅ Including matching sport event:', event.title, eventSport);
          }
          
          return matchesSport;
        });

        console.log('📊 Events after filtering:', filteredEvents.length);
      }

      setEvents(filteredEvents);
    } catch (err) {
      console.error('Error fetching NCAA calendar events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [sportId]); // Re-fetch when sportId changes

  return {
    events,
    loading,
    error,
    refetch: fetchEvents
  };
};